"use client";

import { type ShipmentItem } from "@/app/encomendas/types";
import { primaryColor } from "@/lib/theme";
import LocalityLabel from "@/components/Atoms/LocalityLabel";
import {
  FieldLabel,
  SectionHeader,
  SubHeader,
} from "@/components/Atoms/Typography";
import { api } from "@/services/api";
import useLoginModalStore from "@/store/useLoginModalStore";
import { isAuthenticated } from "@/utils/authUtils";
import { AnimatePresence, motion } from "framer-motion";
import {
  Bus,
  ChevronRight,
  Loader2,
  Package,
  RefreshCw,
  X,
} from "lucide-react";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState, useTransition } from "react";

const BoxPreview3D = dynamic<{
  length: string;
  width: string;
  height: string;
  noInteraction?: boolean;
}>(() => import("@/components/Molecules/BoxPreview3D"), { ssr: false });

export type CotacaoLocation = {
  id: number;
  name: string;
  state?: string;
  ibge_id?: number;
  city_name?: string;
  state_uf?: string;
};

export type CotacaoPackage = {
  id: string;
  name?: string;
  widthCm: number;
  heightCm: number;
  lengthCm: number;
  weightKg: number;
  unitValueBRL: number;
  quantity: number;
  items?: ShipmentItem[];
};

export type ShippingOption = {
  id: string;
  label: string;
  etaBusinessDays: number;
  priceBRL: number;
  quoteId?: number;
};

type QuoteItem = {
  id?: number;
  provider: string;
  price: number;
  currency: string;
  deadline_days: number;
};

interface Props {
  open: boolean;
  onClose: () => void;
  origin: CotacaoLocation;
  destination: CotacaoLocation;
  packages: CotacaoPackage[];
  shippingOptions: ShippingOption[];
  loading: boolean;
  error: string;
  onRetry: () => void;
  /** Called when the user confirms — should run the real /quotes fetch and navigate */
  onContinuar: () => Promise<void>;
}

function formatCurrency(value: number) {
  try {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  } catch {
    return `R$ ${value.toFixed(2).replace(".", ",")}`;
  }
}

function formatDeadline(days: number) {
  if (!days || days < 1) return "";
  if (days === 1) return "Entrega em até 1 dia útil";
  return `Entrega em até ${days} dias úteis`;
}

function locationState(loc: CotacaoLocation) {
  return loc.state_uf ?? loc.state ?? "";
}

export default function CotacaoDialog({
  open,
  onClose,
  origin,
  destination,
  packages,
  shippingOptions,
  loading,
  error,
  onRetry,
  onContinuar,
}: Props) {
  const router = useRouter();
  const { showLoginModal } = useLoginModalStore();
  const [navigating, startNavigation] = useTransition();
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState("");

  const option = shippingOptions[0] ?? null;

  // Auto-select the first option when options load
  useEffect(() => {
    if (option && !selectedOptionId) {
      setSelectedOptionId(option.id);
    }
  }, [option, selectedOptionId]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSelectedOptionId(null);
      setConfirming(false);
      setConfirmError("");
    }
  }, [open]);

  const proceed = useCallback(async () => {
    setConfirming(true);
    setConfirmError("");
    try {
      await onContinuar();
      startNavigation(() => {
        router.push("/encomendas/pacotes");
      });
    } catch {
      setConfirmError("Falha ao confirmar cotação. Tente novamente.");
      setConfirming(false);
    }
  }, [onContinuar, router]);

  // When the user logs in from inside this dialog, finish the same flow the
  // "Continuar" button runs (fetch quotes + persist shipping option) before
  // navigating — otherwise downstream screens bounce back to /encomendas.
  useEffect(() => {
    if (!open) return;
    const handler = () => {
      void proceed();
    };
    window.addEventListener("loginSuccess", handler);
    return () => window.removeEventListener("loginSuccess", handler);
  }, [open, proceed]);

  const handleContinuar = async () => {
    if (!isAuthenticated()) {
      showLoginModal();
      return;
    }
    await proceed();
  };

  const canContinue =
    !loading &&
    !navigating &&
    !confirming &&
    !error &&
    !!option &&
    !!selectedOptionId;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="cotacao-backdrop"
            className="fixed inset-0 z-[190] bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.25, 0.46, 0.45, 0.94] }}
            onClick={onClose}
          />
          <motion.div
            key="cotacao-panel-wrapper"
            className="fixed inset-0 z-[191] flex items-center justify-center p-4 pointer-events-none"
            initial={{ opacity: 0, scale: 0.88 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.88 }}
            transition={{
              type: "spring",
              damping: 28,
              stiffness: 360,
              mass: 0.85,
            }}
          >
            <div
              className="pointer-events-auto bg-white w-full max-w-[72rem] max-h-[92dvh] rounded-card flex flex-col overflow-hidden shadow-float"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <SectionHeader
                className="shrink-0"
                title="Cotação de frete"
                icon={
                  <Package
                    className="w-5 h-5"
                    style={{ color: primaryColor }}
                  />
                }
                action={
                  <button
                    id="btn-cotacao-close"
                    type="button"
                    onClick={onClose}
                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-ground text-slate transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                }
              />

              {/* Body — two columns on desktop, right panel only on mobile */}
              <div className="flex-1 overflow-hidden flex min-h-0">
                {/* Left — packages grid (hidden on mobile) */}
                <div className="hidden md:flex flex-col flex-1 min-w-0 overflow-y-auto">
                  <div className="px-6 py-5 space-y-5 pb-6">
                    {/* Rota */}
                    <div>
                      <SubHeader className="mb-3">Rota</SubHeader>
                      <div className="bg-white rounded-control border border-line px-5 py-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="flex-1 min-w-0">
                            <FieldLabel className="mb-0.5">Envio</FieldLabel>
                            <p className="text-body font-bold text-ink truncate">
                              <LocalityLabel
                                name={origin.name}
                                cityName={origin.city_name}
                              />
                            </p>
                            {locationState(origin) && (
                              <p className="text-fine text-slate">
                                {locationState(origin)}
                              </p>
                            )}
                          </div>

                          <div
                            className="flex items-center gap-1 shrink-0"
                            style={{ width: 96 }}
                          >
                            <Package
                              size={14}
                              strokeWidth={1.75}
                              color={primaryColor}
                              className="shrink-0"
                            />
                            <div
                              className="flex-1 border-t-2 border-dashed"
                              style={{ borderColor: primaryColor }}
                            />
                            <ChevronRight
                              size={14}
                              strokeWidth={2}
                              color={primaryColor}
                              className="shrink-0"
                            />
                          </div>

                          <div className="flex-1 min-w-0 text-right">
                            <FieldLabel className="mb-0.5">Retirada</FieldLabel>
                            <p className="text-body font-bold text-ink truncate">
                              <LocalityLabel
                                name={destination.name}
                                cityName={destination.city_name}
                              />
                            </p>
                            {locationState(destination) && (
                              <p className="text-fine text-slate">
                                {locationState(destination)}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Pacotes grid */}
                    <div>
                      <SubHeader className="mb-3">Pacotes</SubHeader>
                      <div className="grid grid-cols-2 gap-3">
                        {packages.map((p, idx) => (
                          <div
                            key={p.id}
                            className="bg-white rounded-control border border-line p-3 flex flex-col gap-2"
                          >
                            <p className="text-fine font-bold text-ink truncate">
                              {p.name ||
                                `Pacote ${String(idx + 1).padStart(2, "0")}`}
                            </p>
                            <div className="h-px bg-line" />
                            <div
                              className="w-full"
                              style={{ aspectRatio: "2/1" }}
                            >
                              <BoxPreview3D
                                length={String(p.lengthCm)}
                                width={String(p.widthCm)}
                                height={String(p.heightCm)}
                                noInteraction
                              />
                            </div>
                            <div className="h-px bg-line" />
                            <div className="space-y-1">
                              {[
                                ["Comprimento", `${p.lengthCm} cm`],
                                ["Largura", `${p.widthCm} cm`],
                                ["Altura", `${p.heightCm} cm`],
                                ["Peso", `${p.weightKg} kg`],
                              ].map(([label, val]) => (
                                <div
                                  key={label}
                                  className="flex justify-between items-center"
                                >
                                  <span className="text-micro text-slate">
                                    {label}
                                  </span>
                                  <span className="text-micro font-semibold text-ink">
                                    {val}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right — shipping option (full width on mobile, fixed width on desktop) */}
                <div className="flex flex-col w-full md:border-l md:border-line md:w-[360px] md:shrink-0">
                  <div className="px-6 py-5 border-b border-line shrink-0">
                    <SubHeader>Opção de envio</SubHeader>
                  </div>

                  <div className="flex-1 overflow-y-auto p-6">
                    {error && !loading ? (
                      <div className="flex flex-col gap-3">
                        <p className="text-fine text-slate">
                          Falha ao carregar cotação.
                        </p>
                        <button
                          id="btn-cotacao-retry"
                          type="button"
                          onClick={onRetry}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-chip text-fine font-semibold text-ink border border-line hover:bg-ground transition-colors self-start"
                        >
                          <RefreshCw className="w-3.5 h-3.5" />
                          Tentar novamente
                        </button>
                      </div>
                    ) : loading ? (
                      <div className="flex items-center gap-3 animate-pulse">
                        <div className="w-9 h-9 rounded-full bg-line shrink-0" />
                        <div className="flex-1 space-y-1.5">
                          <div className="h-3 w-28 rounded-full bg-line" />
                          <div className="h-3 w-20 rounded-full bg-line" />
                        </div>
                        <div className="h-5 w-20 rounded-full bg-line shrink-0" />
                      </div>
                    ) : option ? (
                      <div className="space-y-2">
                        {shippingOptions.map((opt) => {
                          const isSelected = selectedOptionId === opt.id;
                          return (
                            <button
                              id={`btn-cotacao-option-${opt.id}`}
                              key={opt.id}
                              type="button"
                              onClick={() => setSelectedOptionId(opt.id)}
                              className="w-full flex items-center gap-3 rounded-control px-4 py-3 text-left transition-[border-color,background-color]"
                              style={{
                                border: isSelected
                                  ? `2px solid ${primaryColor}`
                                  : "1px solid #E2E5E9",
                                backgroundColor: isSelected
                                  ? `${primaryColor}08`
                                  : "white",
                              }}
                            >
                              <div
                                className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                                style={{ backgroundColor: primaryColor }}
                              >
                                <Bus className="w-4 h-4 text-white" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-fine font-bold text-ink">
                                  {opt.label}
                                </p>
                                {opt.etaBusinessDays > 0 && (
                                  <p className="text-micro text-slate">
                                    {formatDeadline(opt.etaBusinessDays)}
                                  </p>
                                )}
                              </div>
                              <p className="text-body font-bold text-ink shrink-0">
                                {formatCurrency(opt.priceBRL)}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-fine text-slate">
                        Nenhuma opção disponível.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-line flex items-center justify-end gap-3 shrink-0">
                {confirmError && (
                  <p className="text-fine text-danger mr-auto">
                    {confirmError}
                  </p>
                )}
                <button
                  id="btn-cotacao-dismiss"
                  type="button"
                  onClick={onClose}
                  className="h-11 px-5 rounded-control text-sm font-bold text-ink border border-line hover:bg-ground transition-colors"
                >
                  Fechar
                </button>
                <button
                  id="btn-cotacao-continue"
                  type="button"
                  onClick={handleContinuar}
                  disabled={!canContinue}
                  className="h-11 px-6 rounded-control text-sm font-bold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  style={{ backgroundColor: primaryColor }}
                >
                  {(navigating || confirming) && (
                    <Loader2 className="animate-spin w-4 h-4 shrink-0" />
                  )}
                  Continuar
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function mapQuoteItems(data: unknown): ShippingOption[] {
  const quotes: QuoteItem[] = Array.isArray(data) ? data : [];
  return quotes.map((q) => {
    const provider = (q.provider || "").toString().trim();
    const label =
      provider.toLowerCase() === "brudam"
        ? "Envio Express"
        : provider
          ? `Envio ${provider}`
          : "Envio Express";
    return {
      id: provider,
      label,
      etaBusinessDays: Number(q.deadline_days) || 0,
      priceBRL: Number(q.price) || 0,
      quoteId: q.id != null ? Number(q.id) : undefined,
    };
  });
}

export async function fetchQuickQuote(params: {
  originIbge: number;
  destinationIbge: number;
  packages: CotacaoPackage[];
}): Promise<ShippingOption[]> {
  const packagesPayload = params.packages.map((p) => ({
    length: Number((p.lengthCm / 100).toFixed(3)),
    width: Number((p.widthCm / 100).toFixed(3)),
    height: Number((p.heightCm / 100).toFixed(3)),
    // weightKg e unitValueBRL já vêm somados sobre os itens do pacote, e o
    // backend multiplica weight/value por amount. Mandar a contagem de itens
    // aqui dobraria peso e valor declarado — cada pacote é 1 volume.
    amount: 1,
    weight: Number(p.weightKg),
    value: Number(p.unitValueBRL),
  }));

  const payload = {
    customer_document: "80227796000159",
    origin_ibge: Number(params.originIbge),
    destination_ibge: Number(params.destinationIbge),
    packages: packagesPayload,
  };

  const { data } = await api.post(
    `${process.env.NEXT_PUBLIC_BASE_URL_API}/api/v1/embarca-log/quick-quote`,
    payload
  );

  return mapQuoteItems(data);
}

export async function fetchQuotes(params: {
  originIbge: number;
  destinationIbge: number;
  packages: CotacaoPackage[];
}): Promise<ShippingOption[]> {
  const packagesPayload = params.packages.map((p) => ({
    length: Number((p.lengthCm / 100).toFixed(3)),
    width: Number((p.widthCm / 100).toFixed(3)),
    height: Number((p.heightCm / 100).toFixed(3)),
    amount: 1,
    weight: Number(p.weightKg),
    value: Number(p.unitValueBRL),
  }));

  const payload = {
    customer_document: "80227796000159",
    origin_ibge: params.originIbge,
    destination_ibge: params.destinationIbge,
    packages: packagesPayload,
  };

  const { data } = await api.post(
    `${process.env.NEXT_PUBLIC_BASE_URL_API}/api/v1/embarca-log/quotes`,
    payload
  );

  return mapQuoteItems(data);
}
